import { COMPONENT_TYPE, INPUT_COMPONENT_TYPE } from '@abiglobalhealth/go-types'
import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'

import { Close } from '@/components/icons/Close'
import { RemoveFilters } from '@/components/icons/RemoveFilters'
import { Input } from '@/components/Input'
import { Select } from '@/components/Select'
import { UserFormData } from '@/contexts/userForm'
import { useClickOutside } from '@/hooks/useClickOutside'

import { FiltersT } from './schema'

interface FiltersProps {
  filters: FiltersT
  updateFilter: (id: keyof FiltersT, value: UserFormData) => void
  setFiltersOpen: Dispatch<SetStateAction<boolean>>
  clearFilters: () => void
}

export const Filters = ({ filters, updateFilter, setFiltersOpen, clearFilters }: FiltersProps) => {
  const { t } = useTranslation()
  const containerRef = useClickOutside(() => setFiltersOpen(false))

  return (
    <div className="animate-fade-in bg-gray-text/50 absolute bottom-0 z-20 flex h-full w-full items-end overflow-y-hidden">
      <div
        className="animate-slide-up bg-gray-background flex h-4/5 w-full flex-col gap-4 overflow-y-auto rounded-t-2xl p-4 shadow-lg"
        ref={containerRef}
      >
        <div className="flex w-full justify-between">
          <h2 className="font-bold">{t('prescriptions-us.pharmacy.filter.title')}</h2>
          <button onClick={() => setFiltersOpen(false)}>
            <Close customClassName="w-4" />
          </button>
        </div>

        <Input
          componentType={COMPONENT_TYPE.INPUT}
          type={INPUT_COMPONENT_TYPE.TEXT}
          id="name"
          tabIndex={0}
          placeholder="prescriptions-us.pharmacy.filter.name.placeholder"
          textKey="prescriptions-us.pharmacy.filter.name.title"
          onChange={(value) => updateFilter('name', value)}
          hideError
          value={filters.name}
        />
        <Input
          componentType={COMPONENT_TYPE.INPUT}
          type={INPUT_COMPONENT_TYPE.TEXT}
          id="address"
          tabIndex={1}
          placeholder="prescriptions-us.pharmacy.filter.address.placeholder"
          textKey="prescriptions-us.pharmacy.filter.address.title"
          onChange={(value) => updateFilter('address', value)}
          maxLength={35}
          hideError
          value={filters.address}
        />

        <div className="flex gap-4">
          <Input
            componentType={COMPONENT_TYPE.INPUT}
            type={INPUT_COMPONENT_TYPE.TEXT}
            id="city"
            tabIndex={2}
            placeholder="prescriptions-us.pharmacy.filter.city.placeholder"
            textKey="prescriptions-us.pharmacy.filter.city.title"
            onChange={(value) => updateFilter('city', value)}
            isInRow
            maxLength={35}
            hideError
            value={filters.city}
          />
          <Input
            componentType={COMPONENT_TYPE.INPUT}
            type={INPUT_COMPONENT_TYPE.TEXT}
            id="zipCode"
            tabIndex={3}
            placeholder="prescriptions-us.pharmacy.filter.zipCode.placeholder"
            textKey="prescriptions-us.pharmacy.filter.zipCode.title"
            onChange={(value) => updateFilter('zipCode', value)}
            isInRow
            maxLength={10}
            hideError
            value={filters.zipCode}
          />
        </div>

        <Input
          componentType={COMPONENT_TYPE.INPUT}
          type={INPUT_COMPONENT_TYPE.TEXT}
          id="phoneOrFax"
          tabIndex={4}
          placeholder="prescriptions-us.pharmacy.filter.phoneOrFax.placeholder"
          textKey="prescriptions-us.pharmacy.filter.phoneOrFax.title"
          onChange={(value) => updateFilter('phoneOrFax', value)}
          isInRow
          maxLength={25}
          hideError
          value={filters.phoneOrFax}
        />

        <Select
          id="speciality"
          options={[
            {
              id: 'all',
              textKey: 'prescriptions-us.pharmacy.filter.speciality.all-specialities',
              value: ''
            },
            {
              id: 'mail',
              textKey: 'prescriptions-us.pharmacy.filter.speciality.mail',
              value: '1'
            },
            {
              id: 'retail',
              textKey: 'prescriptions-us.pharmacy.filter.speciality.retail',
              value: '8'
            }
          ]}
          onChange={(value) => updateFilter('speciality', Number(value.value))}
          textKey="prescriptions-us.pharmacy.filter.speciality.title"
          value={String(filters.speciality)}
          errorHidden
        />

        <button onClick={clearFilters} className="text-primary mt-4 flex items-center justify-center gap-3 font-bold uppercase">
          <RemoveFilters className="w-7" />
          {t('prescriptions-us.pharmacy.remove_filters')}
        </button>
      </div>
    </div>
  )
}
